'use client';

import { createRoot, type Root } from 'react-dom/client';

import { createWidgetMount } from '@/shared/utils/widget-mount';

import { FavoriteButtonWidget } from '../components/FavoriteButtonWidget';

const OBSERVED_ATTRIBUTES = ['course-id', 'is-favorite'];

function readBooleanAttribute(element: HTMLElement, name: string): boolean {
  const value = element.getAttribute(name);
  return value !== null && value !== 'false';
}

export class FavoriteButtonWidgetElement extends HTMLElement {
  static observedAttributes = OBSERVED_ATTRIBUTES;

  private root: Root | null = null;
  private mountNode: HTMLDivElement | null = null;
  private renderTimer: number | null = null;
  private unmountTimer: number | null = null;

  connectedCallback() {
    if (this.unmountTimer !== null) {
      window.clearTimeout(this.unmountTimer);
      this.unmountTimer = null;
    }

    if (!this.mountNode) {
      this.mountNode = createWidgetMount(this, {
        rootAttribute: 'data-adipa-favorite-button-root',
      }) as HTMLDivElement;
    }

    if (!this.root) {
      this.root = createRoot(this.mountNode);
    }

    this.scheduleRender();
  }

  disconnectedCallback() {
    if (!this.root || this.unmountTimer !== null) return;

    if (this.renderTimer !== null) {
      window.clearTimeout(this.renderTimer);
      this.renderTimer = null;
    }

    const root = this.root;
    this.unmountTimer = window.setTimeout(() => {
      this.unmountTimer = null;
      if (this.isConnected || this.root !== root) return;
      root.unmount();
      this.root = null;
    }, 0);
  }

  attributeChangedCallback() {
    this.scheduleRender();
  }

  private scheduleRender() {
    if (!this.root || this.renderTimer !== null) return;

    this.renderTimer = window.setTimeout(() => {
      this.renderTimer = null;
      this.renderWidget();
    }, 0);
  }

  private renderWidget() {
    if (!this.root) return;

    const courseId = this.getAttribute('course-id') ?? '';
    const isFavorite = readBooleanAttribute(this, 'is-favorite');

    this.root.render(
      <FavoriteButtonWidget
        courseId={courseId}
        isFavorite={isFavorite}
        onToggle={(id, nextFavorite) => {
          this.dispatchEvent(
            new CustomEvent('adipa-favorite-toggle', {
              bubbles: true,
              composed: true,
              detail: { courseId: id, nextFavorite },
            }),
          );
        }}
      />,
    );
  }
}
